import axios from 'axios';
import { getStoredAuthToken, clearStoredAuthToken, clearAuthTokenOnly, getStoredRefreshToken, setStoredAuthToken } from '../utils/authStorage';
import { emitAuthEvent, AUTH_EVENT_TYPES } from '../utils/authEvents';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

export const getApiBaseUrl = () => API_BASE_URL;

// For <a href> navigations (OAuth, SSE), the CRA dev proxy does NOT intercept
// full-page navigations — only XHR/fetch. So in dev (empty API_BASE_URL), we
// must use the full backend URL. In production, API_BASE_URL is empty too but
// nginx serves both frontend and API on the same origin, so relative URLs work.
const IS_DEV = process.env.NODE_ENV !== 'production';
const OAUTH_BASE_URL = API_BASE_URL || (IS_DEV ? `http://localhost:${process.env.REACT_APP_BACKEND_PORT || 8081}` : '');
export const getOAuthBaseUrl = () => OAUTH_BASE_URL;

apiClient.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;

const performRefresh = async (refreshToken) => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      const response = await axios.post(`${OAUTH_BASE_URL}/api/users/refresh`, {
        refreshToken,
      });
      const newToken = response.data.accessToken;
      setStoredAuthToken(newToken);
      return newToken;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;

    // Prevent infinite loops if refresh fails
    if (status === 401 && originalRequest.url === '/api/users/refresh') {
      clearStoredAuthToken();
      emitAuthEvent({
        type: AUTH_EVENT_TYPES.UNAUTHORIZED,
        payload: {
          reason: 'Session expired or unauthorized.',
          url: originalRequest.url,
        },
      });
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        try {
          const newToken = await performRefresh(refreshToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          const refreshStatus = refreshError?.response?.status;
          if (refreshStatus === 401) {
            // Refresh token is invalid/expired — permanent failure, clear everything
            clearStoredAuthToken();
            emitAuthEvent({
              type: AUTH_EVENT_TYPES.UNAUTHORIZED,
              payload: {
                reason: 'Session expired or unauthorized.',
                url: originalRequest.url,
              },
            });
          } else {
            // Transient failure (network error, 500, etc.) — keep refresh token
            // so the next request can retry. Only clear the expired JWT.
            clearAuthTokenOnly();
          }
          return Promise.reject(refreshError);
        }
      }
    }

    if (status === 401) {
      clearStoredAuthToken();
      emitAuthEvent({
        type: AUTH_EVENT_TYPES.UNAUTHORIZED,
        payload: {
          reason: error?.response?.data?.message || 'Session expired or unauthorized.',
          url: error?.config?.url,
        },
      });
    }

    // 403 Forbidden = user is authenticated but not allowed to perform this action.
    // This includes muted/banned/suspended users trying to write, or moderators
    // trying to access super-user-only endpoints. Do NOT log the user out —
    // just emit a FORBIDDEN event so the UI can show an appropriate message.
    //
    // IMPORTANT: A 403 can also occur when a user's roles were upgraded (e.g.
    // granted ROLE_MODERATOR) after their JWT was issued. The JWT has stale
    // roles baked in. We attempt a token refresh (which loads fresh roles from
    // the DB) and retry the request once. If the retry still 403s, the user
    // genuinely lacks permission.
    if (status === 403 && !originalRequest._retry403) {
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        originalRequest._retry403 = true;
        try {
          const newToken = await performRefresh(refreshToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          // After refreshing the token (which loads fresh roles from the DB),
          // re-fetch the user profile so the frontend's cached user object
          // reflects the updated roles. This fixes the case where a user's
          // roles were upgraded after their JWT was issued — the old profile
          // in memory/localStorage still has the stale roles, causing UI
          // guards (e.g. ModeratorDashboard canAccess) to deny access even
          // though the new JWT has the correct roles.
          try {
            const profileResponse = await apiClient.get('/api/users/me');
            if (profileResponse?.data) {
              emitAuthEvent({
                type: AUTH_EVENT_TYPES.PROFILE_UPDATED,
                payload: profileResponse.data,
              });
            }
          } catch {
            // Profile refresh is best-effort — the retried request below
            // will still work with the new JWT even if the profile update
            // fails. The UI will update on next page load.
          }
          return apiClient(originalRequest);
        } catch {
          // Refresh failed — fall through to emit FORBIDDEN event below
        }
      }
    }

    if (status === 403) {
      emitAuthEvent({
        type: AUTH_EVENT_TYPES.FORBIDDEN,
        payload: {
          reason: error?.response?.data?.message || 'You do not have permission to perform this action.',
          url: error?.config?.url,
        },
      });
    }

    // 429 Too Many Requests = rate limit exceeded. Emit event so the UI can
    // show a user-friendly toast. Do NOT log the user out.
    if (status === 429) {
      const retryAfter = error?.response?.headers?.['retry-after'];
      emitAuthEvent({
        type: AUTH_EVENT_TYPES.RATE_LIMITED,
        payload: {
          reason: error?.response?.data?.message || 'You are making requests too quickly. Please slow down.',
          url: error?.config?.url,
          retryAfter: retryAfter ? parseInt(retryAfter, 10) : null,
        },
      });
    }

    return Promise.reject(error);
  },
);

export { performRefresh };
export default apiClient;
